import { useCallback, useMemo, useState } from 'react'

import {
  ActionIcon,
  Loader,
  Popover,
  ScrollArea,
  TextInput,
  Tooltip,
  UnstyledButton,
} from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { PhoneForwarded } from 'lucide-react'

import { useMutation, useQuery } from '@redwoodjs/web'

import { TRANSFER_CALL_TO_EMPLOYEE } from 'src/graphql/activityLogs.graphql'
import { GET_APPLICANT_RECRUITER_BY_ID } from 'src/graphql/applicants.graphql'
import { GET_EMPLOYEES_ONLY } from 'src/graphql/employees.graphql'
import { buildFullName } from 'src/lib/formatters'
import { formatInternationalPhoneNumber } from 'src/lib/phone.utils'

import { toast } from '../Toast/Toast'

const EMPLOYEE_PAGE_LIMIT = 200
const GENERIC_FAILURE = 'Unable to transfer the call, please try again.'

type TransferTarget = {
  employeeId: string
  firstName?: string
  lastName?: string
  jobTitle?: string
  phone?: string
}

/**
 * Hands a connected call off to another teammate. The transferring employee's
 * leg drops as soon as the applicant is re-dialed, so this is a one-way action
 * with no way back; hence the explicit teammate choice rather than a default.
 */
const TransferCallPopover = ({
  activityLogId,
  applicantId,
  currentEmployeeId,
  onTransferred,
}: {
  activityLogId: string
  applicantId?: string
  currentEmployeeId?: string
  /** Twilio has accepted the redirect; the caller's own leg is about to drop. */
  onTransferred?: () => void
}) => {
  const [opened, setOpened] = useState(false)
  const [search, setSearch] = useState('')
  const [pendingEmployeeId, setPendingEmployeeId] = useState<string>(null)

  const [debouncedSearch] = useDebouncedValue(search.trim(), 250)

  const { data, loading, error, refetch } = useQuery(GET_EMPLOYEES_ONLY, {
    variables: {
      pageInfo: { limit: EMPLOYEE_PAGE_LIMIT, offset: 0 },
      ...(debouncedSearch && { filters: { search: debouncedSearch } }),
    },
    fetchPolicy: 'cache-and-network',
    skip: !opened,
  })

  // Only to pin the assigned recruiter to the top. Absent on VoE calls and on
  // calls placed outside an applicant context.
  const { data: applicantData } = useQuery(GET_APPLICANT_RECRUITER_BY_ID, {
    variables: { id: applicantId },
    skip: !opened || !applicantId,
  })
  const assignedRecruiterEmployeeId =
    applicantData?.applicant?.recruiter?.employeeId

  const [transferCall] = useMutation(TRANSFER_CALL_TO_EMPLOYEE, {
    onCompleted: ({ transferCallToEmployee: result }) => {
      setPendingEmployeeId(null)
      if (result?.success) {
        setOpened(false)
        // Twilio has only accepted the redirect at this point; the teammate
        // has not answered yet.
        toast('Transfer started.', 'success')
        onTransferred?.()
        return
      }
      toast(result?.message || GENERIC_FAILURE, 'error')
    },
    // The API rejects an untransferable call with a ValidationError whose
    // message is written for the recruiter, so surface it as is.
    onError: (error) => {
      setPendingEmployeeId(null)
      toast(error?.message || GENERIC_FAILURE, 'error')
    },
  })

  const targets: TransferTarget[] = useMemo(() => {
    const items: TransferTarget[] = data?.employees?.items ?? []

    return items
      .filter(
        (employee) =>
          // A transfer dials a real phone, so a teammate without one cannot
          // receive the applicant.
          !!employee.phone && employee.employeeId !== currentEmployeeId
      )
      .sort((a, b) => {
        if (a.employeeId === assignedRecruiterEmployeeId) return -1
        if (b.employeeId === assignedRecruiterEmployeeId) return 1
        return buildFullName(a).localeCompare(buildFullName(b))
      })
  }, [data, assignedRecruiterEmployeeId, currentEmployeeId])

  const onSelect = useCallback(
    (employeeId: string) => {
      if (pendingEmployeeId) return
      setPendingEmployeeId(employeeId)
      transferCall({
        variables: { input: { activityLogId, employeeId } },
      })
    },
    [activityLogId, pendingEmployeeId, transferCall]
  )

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      width={280}
      shadow="md"
    >
      <Popover.Target>
        <Tooltip label="Transfer call">
          <ActionIcon
            onClick={() => setOpened((o) => !o)}
            radius="xl"
            variant="outline"
            aria-label="Transfer call"
            classNames={{
              root: `border ${
                opened
                  ? 'border-doubleNickel-brand-500'
                  : 'border-doubleNickel-gray-400'
              }`,
            }}
          >
            <PhoneForwarded
              className={`h-4 w-4 ${
                opened
                  ? 'text-doubleNickel-brand-500'
                  : 'text-doubleNickel-gray-400'
              }`}
            />
          </ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown>
        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold text-doubleNickel-gray-700">
            Transfer to
          </p>
          <TextInput
            size="xs"
            placeholder="Search teammates"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
          {loading && (
            <div className="flex justify-center py-3">
              <Loader size="xs" type="dots" />
            </div>
          )}
          {!loading && error && (
            <div className="flex items-center justify-between py-2">
              <p className="text-xs text-doubleNickel-error-500">
                Could not load teammates.
              </p>
              <UnstyledButton
                onClick={() => refetch()}
                className="text-xs font-medium text-doubleNickel-brand-500"
              >
                Retry
              </UnstyledButton>
            </div>
          )}
          {!loading && !error && targets.length === 0 && (
            <p className="py-2 text-xs text-doubleNickel-gray-500">
              No teammates with a phone number on file.
            </p>
          )}
          {!loading && !error && targets.length > 0 && (
            <ScrollArea.Autosize mah={200}>
              <div className="flex flex-col">
                {targets.map((employee) => (
                  <UnstyledButton
                    key={employee.employeeId}
                    disabled={!!pendingEmployeeId}
                    onClick={() => onSelect(employee.employeeId)}
                    className="flex flex-row items-center justify-between rounded px-2 py-1.5 hover:bg-doubleNickel-gray-100 disabled:opacity-50"
                  >
                    <span className="flex flex-col text-left">
                      <span className="text-xs font-medium text-doubleNickel-gray-900">
                        {buildFullName(employee)}
                        {employee.employeeId ===
                          assignedRecruiterEmployeeId && (
                          <span className="ml-1 text-doubleNickel-brand-500">
                            · assigned
                          </span>
                        )}
                      </span>
                      <span className="text-[11px] text-doubleNickel-gray-500">
                        {formatInternationalPhoneNumber(employee.phone)}
                      </span>
                    </span>
                    {pendingEmployeeId === employee.employeeId && (
                      <Loader size="xs" type="dots" />
                    )}
                  </UnstyledButton>
                ))}
              </div>
            </ScrollArea.Autosize>
          )}
        </div>
      </Popover.Dropdown>
    </Popover>
  )
}

export default TransferCallPopover
